import ClientCombobox from 'components/comboboxes/ClientCombobox';
import ClientTemplateCombobox from 'components/comboboxes/ClientTemplateCombobox';
import MandatoryAsterisk from 'components/common/MandatoryAsterisk';
import MutedText from 'components/common/MutedText';
import HubertCombobox from 'components/HubertUI/HubertCombobox';
import HubertLabel from 'components/HubertUI/HubertLabel';
import { useTranslation } from 'react-i18next';
import { useCreateTemplateState } from './CreateTemplateContext';
import type { TSourceScope } from './types';

const CreateTemplateSource = () => {
  const { t } = useTranslation('Templates');
  const { isHubertAdmin, form } = useCreateTemplateState();
  const { newTemplate, sourceScopeItems, isCopyMode, onSourceScopeChange, onSourceClientChange, onBaseTemplateChange } =
    form;

  if (!isCopyMode) {
    return null;
  }

  return (
    <div className="min-w-0 rounded-xl border border-slate-200/70 bg-white p-4 shadow-xs">
      <div className="mb-3 text-sm font-semibold text-slate-700">{t('source.heading')}</div>
      <div className="grid gap-4">
        <div className="min-w-0">
          <HubertLabel>
            {t('source.optionsLabel')} <MandatoryAsterisk />
          </HubertLabel>
          <div className="mt-2 min-w-0">
            <HubertCombobox
              className="w-full min-w-0"
              itemName={t('source.scopeItemName')}
              items={sourceScopeItems}
              value={newTemplate.sourceScope}
              triggerClassName="w-full min-w-0 max-w-full"
              contentClassName="w-[--radix-popover-trigger-width] min-w-[240px] max-w-[calc(100vw-32px)]"
              onValueChange={(value) => {
                onSourceScopeChange(value as TSourceScope);
              }}
              isMulti={false}
            />
          </div>
        </div>

        {isHubertAdmin && newTemplate.sourceScope === 'other' ? (
          <div className="min-w-0">
            <HubertLabel htmlFor="create-template-source-client-combobox">
              {t('source.clientLabel')}
              <MandatoryAsterisk />
            </HubertLabel>
            <ClientCombobox
              id="create-template-source-client-combobox"
              value={newTemplate.sourceClientId}
              triggerClassName="w-full min-w-0 max-w-full"
              contentClassName="w-[--radix-popover-trigger-width] min-w-[240px] max-w-[calc(100vw-32px)]"
              onValueChange={(value) => {
                onSourceClientChange(value as string);
              }}
            />
          </div>
        ) : null}

        <div className="min-w-0">
          <HubertLabel htmlFor="create-template-base-combobox">
            {t('source.baseTemplateLabel')}
            <MandatoryAsterisk />
          </HubertLabel>
          <ClientTemplateCombobox
            id="create-template-base-combobox"
            onValueChange={(value) => {
              onBaseTemplateChange(value as string);
            }}
            clientId={newTemplate.sourceScope === 'global' ? '' : newTemplate.sourceClientId}
            allowEmptyClientId={newTemplate.sourceScope === 'global'}
            visibilityFilter={newTemplate.sourceScope === 'global' ? ['global'] : []}
            value={newTemplate.baseTemplateId}
            triggerClassName="w-full min-w-0 max-w-full"
            contentClassName="w-[--radix-popover-trigger-width] min-w-[240px] max-w-[calc(100vw-32px)]"
          />
          <MutedText className="block text-xs font-medium text-slate-500" text={t('source.baseTemplateHint')} />
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateSource;
